import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { positionEventEmitter } from '../services/positionEventEmitter';
import { useUserDocument } from './UserDocumentContext';
import { useAuth } from './AuthContext';

export interface OpenPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  current: string;
  pnl: string;
  pnlPercent: string;
  leverage: number;
  timestamp: number;
  orderType: 'market' | 'limit' | 'stop';
  isAI?: boolean; // Flag to indicate if position was created by AI
  /**
   * Exit levels set from the chart, held as prices. The monitor below closes the position as
   * soon as the market reaches one, which is what makes a level a real resting exit rather
   * than only a drawing on the chart.
   */
  stopLoss?: string;
  takeProfit?: string;
}

export interface PendingOrder {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  price: string;
  stopPrice?: string;
  orderType: 'limit' | 'stop';
  status: 'Pending' | 'Filled' | 'Cancelled';
  leverage: number;
  timestamp: number;
}

export interface ClosedPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  close: string;
  pnl: string;
  pnlPercent: string;
  closeTime: string;
  status: 'Success' | 'Cancelled';
  timestamp: number;
  isAI?: boolean; // Flag to indicate if position was created by AI
}

interface PositionContextType {
  openPositions: OpenPosition[];
  pendingOrders: PendingOrder[];
  closedPositions: ClosedPosition[];
  closingPositions: Set<string>;
  totalUnrealizedPnL: number;
  addOpenPosition: (position: Omit<OpenPosition, 'id' | 'timestamp'>) => Promise<OpenPosition>;
  addPendingOrder: (order: Omit<PendingOrder, 'id' | 'timestamp'>) => void;
  closePosition: (positionId: string, closePrice: string) => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  editOrder: (
    orderId: string,
    newPrice: string,
    newSize: string,
    newOrderType?: 'limit' | 'stop'
  ) => void;
  fillOrder: (orderId: string, fillPrice: string) => void;
  /** `null` clears a level, `undefined` leaves it as it is. */
  setPositionExitLevels: (
    positionId: string,
    levels: { stopLoss?: string | null; takeProfit?: string | null }
  ) => void;
  updateCurrentPrices: (priceUpdates: { [symbol: string]: string }) => void;
  testThresholdsForSymbol: (symbol: string) => { profitThreshold: number; lossThreshold: number }; // 🔥 NEW: Test function
}

const PositionContext = createContext<PositionContextType | undefined>(undefined);

export const usePositionContext = () => {
  const context = useContext(PositionContext);
  if (context === undefined) {
    throw new Error('usePositionContext must be used within a PositionProvider');
  }
  return context;
};

interface PositionProviderProps {
  children: ReactNode;
}

/**
 * Merge a stop loss and/or take profit into a position. An omitted side is left as it was,
 * and an explicit null clears it, which is how a level is removed from the chart.
 */
const applyExitLevels = (
  position: OpenPosition,
  levels: { stopLoss?: string | null; takeProfit?: string | null }
): OpenPosition => {
  const next = { ...position };
  if (levels.stopLoss !== undefined) next.stopLoss = levels.stopLoss ?? undefined;
  if (levels.takeProfit !== undefined) next.takeProfit = levels.takeProfit ?? undefined;
  return next;
};

export const PositionProvider: React.FC<PositionProviderProps> = ({ children }) => {
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const [closingPositions, setClosingPositions] = useState<Set<string>>(new Set());
  
  // Get live market data for real-time price updates
  const { getMarketDataForSymbol } = useBinanceMarketData();
  
  // Get account context for profit updates
  const { addProfit, forceFirebaseSync, openPosition: openPositionBalance, closePosition: closePositionBalance, syncPositionState, setBalanceUpdateTrigger } = useAccount();
  
  // Get account type context
  const { accountType } = useAccountType();
  
  // Get auth context for user ID
  const { user } = useAuth();
  
  // Get user document context for persistence
  const { 
    userDocument, 
    updateOpenPosition, 
    removeOpenPosition, 
    addClosedTrade, 
    addTradingHistoryEntry 
  } = useUserDocument();

  // Ref to track previous values to prevent unnecessary emissions
  const prevValuesRef = useRef<{ totalUsedMargin: number; totalUnrealizedPnL: number; accountType: string } | null>(null);

  // Latest unrealized P&L and in-flight closes, read by the equity writer below on its own
  // schedule rather than being chased on every render.
  const unrealizedPnLRef = useRef(0);
  const usedMarginRef = useRef(0);
  const closingPositionsRef = useRef<Set<string>>(new Set());
  closingPositionsRef.current = closingPositions;

  /** True once the stored positions have been read, so an empty list can be trusted. */
  const positionsLoadedRef = useRef(false);

  /** Orders whose fill is in flight, so the execution check cannot fill one twice. */
  const fillingOrdersRef = useRef<Set<string>>(new Set());

  /** How often used margin and equity are restated in Firestore from the open positions. */
  const POSITION_SYNC_INTERVAL_MS = 2000;

  // Default thresholds (fallback values) - Updated to match VidyaHeart strategy
  const DEFAULT_PROFIT_THRESHOLD = 1.99; // Match VidyaHeart strategy
  const DEFAULT_LOSS_THRESHOLD = 1.99;   // Match VidyaHeart strategy

  // Cache for thresholds to prevent repeated lookups
  const thresholdsCache = useRef<{ [symbol: string]: { thresholds: any; timestamp: number } }>({});
  const CACHE_DURATION = 5000; // 5 seconds cache

  // Function to get thresholds from strategy configuration in user document
  const getThresholdsForSymbol = useCallback((symbol: string) => {
    try {
      // Check cache first to prevent excessive logging
      const cached = thresholdsCache.current[symbol];
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        return cached.thresholds;
      }

      if (userDocument && accountType) {
        // 🔥 CRITICAL FIX: Use correct data structure - demoAccountData/realAccountData
        const accountDataKey = accountType === 'live' ? 'realAccountData' : 'demoAccountData';
        const accountData = (userDocument as any)?.[accountDataKey];
        const aiTradingData = accountData?.aiTradingData;
        
        // Only log once per cache miss to reduce console spam
        if (!cached) {
          console.log(`🔍 Searching for thresholds for ${symbol}:`, {
            hasUserDocument: !!userDocument,
            accountType,
            hasAccountData: !!accountData,
            hasAiTradingData: !!aiTradingData,
            activeStrategiesCount: aiTradingData?.activeStrategies?.length || 0,
            hasAssets: !!aiTradingData?.assets,
            assetKeys: aiTradingData?.assets ? Object.keys(aiTradingData.assets) : [],
            // 🔥 DEBUG: Show actual userDocument structure
            userDocumentKeys: userDocument ? Object.keys(userDocument) : [],
            accountDataKey,
            accountDataStructure: accountData ? Object.keys(accountData) : [],
            // 🔥 DEBUG: Show if we found the strategy
            foundStrategy: aiTradingData?.activeStrategies?.find((s: any) => s.symbol === symbol),
            // 🔥 DEBUG: Show the actual userDocument structure
            userDocumentStructure: userDocument ? {
              keys: Object.keys(userDocument),
              hasDemoAccountData: !!(userDocument as any).demoAccountData,
              hasRealAccountData: !!(userDocument as any).realAccountData,
              hasAccounts: !!(userDocument as any).accounts,
              accountsKeys: (userDocument as any).accounts ? Object.keys((userDocument as any).accounts) : []
            } : null
          });
        }
        
        // `profitThreshold` and `lossThreshold` are dollar amounts on the position's P&L.
        // A blank or non-numeric field falls back to the default; `|| DEFAULT` used to do the
        // same job but also swallowed a deliberate 0.
        const readThresholds = (config: any) => {
          const profit = Number(config?.profitThreshold);
          const loss = Number(config?.lossThreshold);
          return {
            profitThreshold: Number.isFinite(profit) && profit > 0 ? profit : DEFAULT_PROFIT_THRESHOLD,
            lossThreshold: Number.isFinite(loss) && loss > 0 ? loss : DEFAULT_LOSS_THRESHOLD
          };
        };

        const remember = (thresholds: { profitThreshold: number; lossThreshold: number }, source: string) => {
          thresholdsCache.current[symbol] = { thresholds, timestamp: Date.now() };
          if (!cached) {
            console.log(
              `✅ Thresholds: ${symbol} | Profit: +$${thresholds.profitThreshold} | Loss: -$${thresholds.lossThreshold} | from ${source}`
            );
          }
          return thresholds;
        };

        // The settings dialog saves the strategy to `assets[symbol].activeStrategyInstance`
        // and reads it back from there, so that copy is the one the user actually edited.
        // `activeStrategies` is a flat mirror kept for older code; it was being consulted
        // first, so a stale entry left in it by an earlier save handed back thresholds that
        // no longer matched the strategy on screen.
        const assetStrategy = aiTradingData?.assets?.[symbol]?.activeStrategyInstance;
        if (assetStrategy?.config && (assetStrategy.isActive || assetStrategy.aiTradeStartEnabled)) {
          return remember(readThresholds(assetStrategy.config), 'saved strategy');
        }

        if (Array.isArray(aiTradingData?.activeStrategies)) {
          const activeStrategy = aiTradingData.activeStrategies.find((strategy: any) =>
            strategy.symbol === symbol && (strategy.isActive || strategy.aiTradeStartEnabled)
          ) as any;

          if (activeStrategy?.config) {
            return remember(readThresholds(activeStrategy.config), 'activeStrategies mirror');
          }
        }

        if (!cached) {
          console.log(`⚠️ No saved strategy configuration found for ${symbol}`);
        }
      }
    } catch (error) {
      console.warn(`❌ Could not get thresholds for ${symbol}:`, error);
    }
    
    // Return default thresholds if strategy not found or no config
    const defaultThresholds = {
      profitThreshold: DEFAULT_PROFIT_THRESHOLD,
      lossThreshold: DEFAULT_LOSS_THRESHOLD
    };
    
    // The warning is decided before the cache is written. It used to be checked afterwards,
    // against the entry that had just been stored, so it was always considered already
    // cached and the one message that says the configuration was never found never appeared.
    const wasCached = !!thresholdsCache.current[symbol];

    thresholdsCache.current[symbol] = {
      thresholds: defaultThresholds,
      timestamp: Date.now()
    };

    if (!wasCached) {
      console.log(`⚠️ Using default thresholds for ${symbol} (no saved configuration found):`, defaultThresholds);
    }
    
    return defaultThresholds;
  }, [userDocument, accountType]);

  // 🔥 NEW: Function to test threshold system for debugging
  const testThresholdsForSymbol = useCallback((symbol: string) => {
    const thresholds = getThresholdsForSymbol(symbol);
    console.log(`🧪 Threshold Test for ${symbol}:`, {
      profitThreshold: thresholds.profitThreshold,
      lossThreshold: thresholds.lossThreshold,
      source: 'PositionContext Auto-Close System'
    });
    return thresholds;
  }, [getThresholdsForSymbol]);

  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  // Load existing positions from Firestore when user document is available
  useEffect(() => {
    if (userDocument && accountType) {
      // 🔥 CRITICAL FIX: Use correct data structure - demoAccountData/realAccountData
      const accountDataKey = accountType === 'live' ? 'realAccountData' : 'demoAccountData';
      const accountData = (userDocument as any)?.[accountDataKey];
      
      
      if (accountData) {
        // Convert Firestore positions to local format
        const firestorePositions: OpenPosition[] = (accountData.openPositions || []).map((pos: any) => {
          // Ensure openedAt is a Date object
          let openedAtDate: Date;
          
          if (pos.openedAt instanceof Date) {
            openedAtDate = pos.openedAt;
          } else if (pos.openedAt && typeof pos.openedAt === 'object' && 'seconds' in pos.openedAt) {
            // Handle Firestore Timestamp object
            const timestamp = pos.openedAt as { seconds: number; nanoseconds?: number };
            openedAtDate = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
          } else {
            openedAtDate = new Date(pos.openedAt);
          }
          
          return {
            id: pos.id,
            symbol: pos.symbol,
            type: pos.type === 'long' ? 'Buy' : 'Sell',
            size: pos.size.toString(),
            entry: pos.entryPrice.toString(),
            current: pos.currentPrice.toString(),
            pnl: pos.unrealizedPnL >= 0 ? `+$${pos.unrealizedPnL.toFixed(2)}` : `-$${Math.abs(pos.unrealizedPnL).toFixed(2)}`,
            pnlPercent: pos.unrealizedPnLPercent >= 0 ? `+${pos.unrealizedPnLPercent.toFixed(2)}%` : `-${Math.abs(pos.unrealizedPnLPercent).toFixed(2)}%`,
            leverage: pos.leverage,
            timestamp: openedAtDate.getTime(),
            orderType: 'market' as const,
            isAI: pos.source === 'ai',
            // Exit levels have to survive the round trip. This list replaces the whole of
            // `openPositions` every time the document changes, so dropping them here erased
            // any level dragged onto the chart the moment anything else was written.
            stopLoss: pos.stopLoss != null ? String(pos.stopLoss) : undefined,
            takeProfit: pos.takeProfit != null ? String(pos.takeProfit) : undefined
          };
        });

        // Convert Firestore closed trades to local format
        const firestoreClosedPositions: ClosedPosition[] = (accountData.closedTrades || []).map((trade: any) => {
          // Ensure closedAt is a valid Date object
          let closedAtDate: Date;
          
          if (trade.closedAt instanceof Date) {
            closedAtDate = trade.closedAt;
          } else if (trade.closedAt && typeof trade.closedAt === 'object' && 'seconds' in trade.closedAt) {
            // Handle Firestore Timestamp object
            const timestamp = trade.closedAt as { seconds: number; nanoseconds?: number };
            closedAtDate = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
          } else {
            closedAtDate = new Date(trade.closedAt);
          }
          
          // Validate the date
          if (isNaN(closedAtDate.getTime())) {
            console.warn(`❌ Invalid closedAt date for trade ${trade.id}:`, trade.closedAt);
            closedAtDate = new Date(); // Fallback to current time
          }
          
          return {
            id: trade.id,
            symbol: trade.symbol,
            type: trade.type === 'long' ? 'Buy' : 'Sell',
            size: trade.size.toString(),
            entry: trade.entryPrice.toString(),
            close: trade.exitPrice.toString(),
            pnl: trade.realizedPnL >= 0 ? `+$${trade.realizedPnL.toFixed(2)}` : `-$${Math.abs(trade.realizedPnL).toFixed(2)}`,
            pnlPercent: trade.realizedPnLPercent >= 0 ? `+${trade.realizedPnLPercent.toFixed(2)}%` : `-${Math.abs(trade.realizedPnLPercent).toFixed(2)}%`,
            closeTime: closedAtDate.toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            }),
            status: 'Success' as const,
            timestamp: closedAtDate.getTime(),
            isAI: trade.source === 'ai'
          };
        });

        // Convert Firestore open orders to local format
        const firestorePendingOrders: PendingOrder[] = (accountData.openOrders || []).map((order: any) => {
          // Handle createdAt timestamp conversion
          let createdAtDate: Date;
          
          if (order.createdAt instanceof Date) {
            createdAtDate = order.createdAt;
          } else if (order.createdAt && typeof order.createdAt === 'object' && 'seconds' in order.createdAt) {
            // Handle Firestore Timestamp object
            const timestamp = order.createdAt as { seconds: number; nanoseconds?: number };
            createdAtDate = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
          } else {
            createdAtDate = new Date(order.createdAt);
          }
          
          return {
            id: order.id,
            symbol: order.symbol,
            type: order.type.includes('buy') ? 'Buy' : 'Sell',
            size: order.size.toString(),
            price: order.price.toString(),
            orderType: order.type.includes('limit') ? 'limit' : 'stop',
            status: 'Pending' as const,
            leverage: 100, // Default leverage since OpenOrder doesn't have leverage
            timestamp: createdAtDate.getTime(),
          };
        });

        setOpenPositions(firestorePositions);
        setClosedPositions(firestoreClosedPositions);
        setPendingOrders(firestorePendingOrders);
        positionsLoadedRef.current = true;
        
        // 🔥 NEW: Detect and restore AI strategy states on page reload
        const aiTradingData = accountData?.aiTradingData;
        if (aiTradingData?.activeStrategies && Array.isArray(aiTradingData.activeStrategies)) {
          const activeStrategies = aiTradingData.activeStrategies.filter((strategy: any) => 
            strategy.isActive && strategy.aiTradeStartEnabled
          );
          
          if (activeStrategies.length > 0) {
            console.log(`🔄 Page Reload: Detected ${activeStrategies.length} active AI strategies:`, 
              activeStrategies.map((s: any) => `${s.name} (${s.symbol})`).join(', ')
            );
            
            // Emit event to notify AI trading contexts about restored strategies
            positionEventEmitter.emit('aiStrategiesRestored', {
              strategies: activeStrategies,
              accountType,
              timestamp: Date.now()
            });
          }
        }
        
      }
    }
  }, [userDocument, accountType]);
  // Clear positions when account type changes (before loading new data)
  useEffect(() => {
    setOpenPositions([]);
    setClosedPositions([]);
    setPendingOrders([]);
    // The other account's positions have not been read yet, so this empty list says nothing
    // about how much margin it has locked.
    positionsLoadedRef.current = false;
  }, [accountType]);

  // Calculate total used margin from all open positions
  const calculateTotalUsedMargin = useCallback((positions: OpenPosition[]): number => {
    return positions.reduce((total, position) => {
      const entryPrice = parseFloat(position.entry);
      const size = parseFloat(position.size);
      const leverage = position.leverage;
      const marginUsed = (entryPrice * size) / leverage;
      return total + marginUsed;
    }, 0);
  }, []);

  // Calculate margin from pending orders
  const calculatePendingOrdersMargin = useCallback((orders: PendingOrder[]): number => {
    return orders.reduce((total, order) => {
      const orderPrice = parseFloat(order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price);
      const orderSize = parseFloat(order.size);
      const leverage = order.leverage;
      const marginUsed = (orderPrice * orderSize) / leverage;
      return total + marginUsed;
    }, 0);
  }, []);

  // Calculate total unrealized P&L from all open positions
  const calculateTotalUnrealizedPnL = useCallback((positions: OpenPosition[]): number => {
    return positions.reduce((total, position) => {
      const entryPrice = parseFloat(position.entry);
      const currentPrice = parseFloat(position.current);
      const size = parseFloat(position.size);
      
      let pnl: number;
      if (position.type === 'Buy') {
        pnl = (currentPrice - entryPrice) * size;
      } else {
        pnl = (entryPrice - currentPrice) * size;
      }
      
      return total + pnl;
    }, 0);
  }, []);

  // Function to calculate P&L for a position
  const calculatePnL = useCallback((entryPrice: number, currentPrice: number, size: number, type: 'Buy' | 'Sell') => {
    let pnl: number;
    if (type === 'Buy') {
      pnl = (currentPrice - entryPrice) * size;
    } else {
      pnl = (entryPrice - currentPrice) * size;
    }
    const pnlPercent = ((pnl / (entryPrice * size)) * 100).toFixed(2);
    const pnlFormatted = pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`;
    const pnlPercentFormatted = pnl >= 0 ? `+${pnlPercent}%` : `-${Math.abs(parseFloat(pnlPercent))}%`;
    
    return { pnl: pnlFormatted, pnlPercent: pnlPercentFormatted };
  }, []);

  const addOpenPosition = async (positionData: Omit<OpenPosition, 'id' | 'timestamp'>) => {
    // Calculate initial P&L
    const entryPrice = parseFloat(positionData.entry);
    const currentPrice = parseFloat(positionData.current);
    const size = parseFloat(positionData.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, positionData.type);
    
    const newPosition: OpenPosition = {
      ...positionData,
      id: generateId(),
      timestamp: Date.now(),
      pnl,
      pnlPercent,
      orderType: positionData.orderType || 'market', // Default to market if not specified
    };
    
    // Log trade execution
    if (positionData.isAI) {
    } else {
    }
    
    // Save to Firestore
    try {
      const firestorePosition = {
        id: newPosition.id,
        symbol: newPosition.symbol,
        type: newPosition.type === 'Buy' ? 'long' as const : 'short' as const,
        size: parseFloat(newPosition.size),
        entryPrice: parseFloat(newPosition.entry),
        currentPrice: parseFloat(newPosition.current),
        stopLoss: newPosition.stopLoss ? parseFloat(newPosition.stopLoss) : null,
        takeProfit: newPosition.takeProfit ? parseFloat(newPosition.takeProfit) : null,
        openedAt: new Date(newPosition.timestamp),
        lastUpdated: new Date(),
        unrealizedPnL: parseFloat(pnl.replace(/[+$,]/g, '')),
        unrealizedPnLPercent: parseFloat(pnlPercent.replace(/[+%,]/g, '')),
        marginUsed: (parseFloat(newPosition.entry) * parseFloat(newPosition.size)) / newPosition.leverage,
        leverage: newPosition.leverage,
        swap: 0,
        commission: 0,
        comment: positionData.isAI ? 'AI Trade' : 'Manual Trade',
        source: positionData.isAI ? 'ai' as const : 'manual' as const
      };
      
      await updateOpenPosition(accountType, firestorePosition);
      
      // Update simplified balance system
      const marginRequired = (parseFloat(newPosition.entry) * parseFloat(newPosition.size)) / newPosition.leverage;
      await openPositionBalance(accountType, marginRequired, newPosition.id, newPosition.symbol);
      
      // Trigger balance update
      setBalanceUpdateTrigger(prev => prev + 1);
      
      // Add trading history entry
      await addTradingHistoryEntry(accountType, {
        id: `hist_${newPosition.id}`,
        type: 'trade',
        symbol: newPosition.symbol,
        amount: parseFloat(pnl.replace(/[+$,]/g, '')),
        price: parseFloat(newPosition.entry),
        quantity: parseFloat(newPosition.size),
        timestamp: new Date(newPosition.timestamp),
        status: 'completed',
        description: `${positionData.isAI ? 'AI' : 'Manual'} ${newPosition.type} trade executed`,
        source: positionData.isAI ? 'ai' : 'manual',
        reference: newPosition.id
      });
      
    } catch (error) {
      console.error('Error saving position to Firestore:', error);
    }
    
    setOpenPositions(prev => {
      const updatedPositions = [newPosition, ...prev];
      
      // Emit position update after state change
      const timeoutId = setTimeout(() => {
        const totalUsedMargin = calculateTotalUsedMargin(updatedPositions);
        const totalUnrealizedPnL = calculateTotalUnrealizedPnL(updatedPositions);
        positionEventEmitter.emit('positionsUpdated', { 
          totalUsedMargin, 
          totalUnrealizedPnL, 
          accountType 
        });
      }, 0);
      
      // Store timeout ID for potential cleanup
      return updatedPositions;
    });
    
    return newPosition; // Return the position so AI context can track it
  };

  // `fillOrder` is memoized on the pending orders alone so that the execution timer that
  // depends on it is not torn down and rebuilt on every render; reaching the opener through
  // a ref keeps it out of the dependency list.
  const addOpenPositionRef = useRef(addOpenPosition);
  addOpenPositionRef.current = addOpenPosition;

  const addPendingOrder = (orderData: Omit<PendingOrder, 'id' | 'timestamp'>) => {
    const newOrder: PendingOrder = {
      ...orderData,
      id: generateId(),
      timestamp: Date.now(),
    };
    setPendingOrders(prev => [newOrder, ...prev]);
  };

  const closePosition = async (positionId: string, closePrice: string) => {
    const position = openPositions.find(p => p.id === positionId);
    if (!position) return;

    // Check if position is already being closed to prevent multiple closes
    if (closingPositions.has(positionId)) {
      console.warn(`Position ${positionId} is already being closed`);
      return;
    }

    // Mark position as being closed
    setClosingPositions(prev => new Set(prev).add(positionId));

    try {
      // Calculate P&L using the helper function
      const entryPrice = parseFloat(position.entry);
      const closePriceNum = parseFloat(closePrice);
      const size = parseFloat(position.size);
      const { pnl, pnlPercent } = calculatePnL(entryPrice, closePriceNum, size, position.type);

      // Calculate numeric P&L for profit addition
      let numericPnL: number;
      if (position.type === 'Buy') {
        numericPnL = (closePriceNum - entryPrice) * size;
      } else {
        numericPnL = (entryPrice - closePriceNum) * size;
      }

      // Calculate margin that was used for this position (to return it to balance)
      const leverage = position.leverage;
      const marginUsed = (entryPrice * size) / leverage;

      // Whatever is still open keeps contributing its unrealized P&L to equity, so that has
      // to be settled at the same moment the closing position's P&L is realised. Without it
      // equity briefly collapses to the bare balance and the header jumps.
      const remainingUnrealizedPnL = calculateTotalUnrealizedPnL(
        openPositions.filter(p => p.id !== positionId)
      );

      // Update simplified balance system (margin release + P&L)
      const description = `${position.symbol} ${position.type} position closed at ${closePrice}`;
      await closePositionBalance(
        accountType, marginUsed, numericPnL, position.id, position.symbol, remainingUnrealizedPnL
      );

      // Keeps the local figures the UI reads in step; the write above is the persisted one.
      await addProfit(numericPnL, marginUsed, description, accountType, remainingUnrealizedPnL);
      
      // Trigger balance update
      setBalanceUpdateTrigger(prev => prev + 1);
      
      // Force Firebase sync after position closing to ensure balance consistency
      setTimeout(() => {
        forceFirebaseSync();
      }, 3000); // 3 second delay to allow addProfit and Firebase update to complete

      // Add to closed positions
      const closedPosition: ClosedPosition = {
        id: position.id,
        symbol: position.symbol,
        type: position.type,
        size: position.size,
        entry: position.entry,
        close: closePrice,
        pnl,
        pnlPercent,
        closeTime: new Date().toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        }),
        status: 'Success',
        timestamp: Date.now(),
        isAI: position.isAI, // Preserve AI flag
      };

      // Save closed trade to Firestore
      try {
        // Ensure position.timestamp is a valid number
        const openedAtTimestamp = typeof position.timestamp === 'number' && !isNaN(position.timestamp) 
          ? position.timestamp 
          : Date.now() - (5 * 60 * 1000); // Default to 5 minutes ago if invalid

        // Create valid Date objects
        const openedAtDate = new Date(openedAtTimestamp);
        const closedAtDate = new Date();

        // Validate dates before proceeding
        if (isNaN(openedAtDate.getTime())) {
          console.error('❌ Invalid openedAt date for position:', position.id, 'timestamp:', position.timestamp);
          throw new Error('Invalid position timestamp');
        }
        
        if (isNaN(closedAtDate.getTime())) {
          console.error('❌ Invalid closedAt date for position:', position.id);
          throw new Error('Invalid close date');
        }

        const firestoreClosedTrade = {
          id: position.id,
          symbol: position.symbol,
          type: position.type === 'Buy' ? 'long' as const : 'short' as const,
          size: parseFloat(position.size),
          entryPrice: parseFloat(position.entry),
          exitPrice: parseFloat(closePrice),
          stopLoss: null, // Use null instead of undefined
          takeProfit: null, // Use null instead of undefined
          openedAt: openedAtDate,
          closedAt: closedAtDate,
          duration: Math.floor((closedAtDate.getTime() - openedAtDate.getTime()) / (1000 * 60)), // duration in minutes
          realizedPnL: numericPnL,
          realizedPnLPercent: parseFloat(pnlPercent.replace(/[+%,]/g, '')),
          swap: 0,
          commission: 0,
          totalPnL: numericPnL,
          comment: position.isAI ? 'AI Trade' : 'Manual Trade',
          source: position.isAI ? 'ai' as const : 'manual' as const,
          closeReason: 'manual' as const
        };

        
        await addClosedTrade(accountType, firestoreClosedTrade);
        
        // Remove from open positions in Firestore
        await removeOpenPosition(accountType, positionId);
        
        // Add trading history entry
        await addTradingHistoryEntry(accountType, {
          id: `hist_close_${position.id}`,
          type: 'trade',
          symbol: position.symbol,
          amount: numericPnL,
          price: parseFloat(closePrice),
          quantity: parseFloat(position.size),
          timestamp: new Date(),
          status: 'completed',
          description: `${position.isAI ? 'AI' : 'Manual'} ${position.type} trade closed`,
          source: position.isAI ? 'ai' : 'manual',
          reference: position.id
        });
        
      } catch (error) {
        console.error('Error saving closed trade to Firestore:', error);
      }

      setClosedPositions(prev => [closedPosition, ...prev]);
      setOpenPositions(prev => {
        const updatedPositions = prev.filter(p => p.id !== positionId);
        
        // Immediately emit position update to ensure margin is returned instantly
        // This ensures the margin is immediately returned to balance when position closes
        setTimeout(() => {
          const totalUsedMargin = calculateTotalUsedMargin(updatedPositions);
          const totalUnrealizedPnL = calculateTotalUnrealizedPnL(updatedPositions);
          positionEventEmitter.emit('positionsUpdated', { 
            totalUsedMargin, 
            totalUnrealizedPnL, 
            accountType 
          });
        }, 0);
        
        return updatedPositions;
      });

    } finally {
      // Always remove position from closingPositions Set, even if there was an error
      setClosingPositions(prev => {
        const newSet = new Set(prev);
        newSet.delete(positionId);
        return newSet;
      });
    }
  };

  const cancelOrder = async (orderId: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) return;

    // No balance call here. A resting order never locks margin against the stored balance —
    // only the local margin figure counts it, and that is recomputed from the order and
    // position lists whenever either changes. Releasing margin here subtracted an amount
    // that had never been taken, leaving the account short of margin it was still using.

    // Add to closed positions as cancelled
    const cancelledOrder: ClosedPosition = {
      id: order.id,
      symbol: order.symbol,
      type: order.type,
      size: order.size,
      entry: order.price,
      close: order.price,
      pnl: '$0.00',
      pnlPercent: '0.00%',
      closeTime: new Date().toLocaleString(),
      status: 'Cancelled',
      timestamp: Date.now(),
    };

    setClosedPositions(prev => [cancelledOrder, ...prev]);
    setPendingOrders(prev => prev.filter(o => o.id !== orderId));
  };

  /**
   * `newOrderType` lets a caller change limit <-> stop, which dragging an order across the
   * market price on the chart has to do. Which side of the market a resting order sits on
   * decides its kind: a buy below the market is a limit, a buy above it is a stop, and the
   * reverse for a sell. Moving one across without changing its kind would leave it in a state
   * that the fill check below treats as immediately triggerable.
   */
  const editOrder = (
    orderId: string,
    newPrice: string,
    newSize: string,
    newOrderType?: 'limit' | 'stop'
  ) => {
    setPendingOrders(prev =>
      prev.map(order => {
        if (order.id !== orderId) return order;

        const orderType = newOrderType ?? order.orderType;

        return {
          ...order,
          orderType,
          // `price` is kept current for both kinds because the margin and cancellation paths
          // read it directly; the fill check reads `stopPrice` for a stop order.
          price: newPrice,
          stopPrice: orderType === 'stop' ? newPrice : undefined,
          size: newSize
        };
      })
    );
  };

  const fillOrder = useCallback((orderId: string, fillPrice: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) return;

    // The execution check runs on a timer and `setPendingOrders` below does not take effect
    // until the next render, so without this the same order can be filled more than once.
    if (fillingOrdersRef.current.has(orderId)) return;
    fillingOrdersRef.current.add(orderId);

    const entryPrice = parseFloat(fillPrice);
    const size = parseFloat(order.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, entryPrice, size, order.type);

    setPendingOrders(prev => prev.filter(o => o.id !== orderId));

    // A filled order becomes an open position like any other, so it goes through the same
    // path: margin locked against the balance, position written to Firestore, entry recorded
    // in the history. This used to move the order between two pieces of React state and
    // nothing else, so its margin was never taken from the account — the header kept showing
    // zero margin against a live position — and the position itself disappeared on reload.
    addOpenPositionRef.current({
      symbol: order.symbol,
      type: order.type,
      size: order.size,
      entry: fillPrice,
      current: fillPrice,
      pnl,
      pnlPercent,
      leverage: order.leverage,
      orderType: order.orderType
    })
      .catch(error => console.error('Error opening position from filled order:', error))
      .finally(() => fillingOrdersRef.current.delete(orderId));
  }, [pendingOrders, calculatePnL]);

  const persistExitLevels = useCallback(
    async (position: OpenPosition) => {
      try {
        const entryPrice = parseFloat(position.entry);
        const currentPrice = parseFloat(position.current);
        const size = parseFloat(position.size);
        const notional = entryPrice * size;
        const unrealizedPnL =
          position.type === 'Buy'
            ? (currentPrice - entryPrice) * size
            : (entryPrice - currentPrice) * size;

        await updateOpenPosition(accountType, {
          id: position.id,
          symbol: position.symbol,
          type: position.type === 'Buy' ? 'long' : 'short',
          size,
          entryPrice,
          currentPrice,
          stopLoss: position.stopLoss ? parseFloat(position.stopLoss) : null,
          takeProfit: position.takeProfit ? parseFloat(position.takeProfit) : null,
          openedAt: new Date(position.timestamp),
          lastUpdated: new Date(),
          unrealizedPnL,
          unrealizedPnLPercent: notional === 0 ? 0 : (unrealizedPnL / notional) * 100,
          marginUsed: notional / position.leverage,
          leverage: position.leverage,
          swap: 0,
          commission: 0,
          comment: position.isAI ? 'AI Trade' : 'Manual Trade',
          source: position.isAI ? 'ai' : 'manual'
        });
      } catch (error) {
        console.error('Error saving exit levels to Firestore:', error);
      }
    },
    [accountType, updateOpenPosition]
  );

  const setPositionExitLevels = useCallback(
    (positionId: string, levels: { stopLoss?: string | null; takeProfit?: string | null }) => {
      const existing = openPositions.find(position => position.id === positionId);

      setOpenPositions(prev =>
        prev.map(position => (position.id === positionId ? applyExitLevels(position, levels) : position))
      );

      // The level has to reach Firestore as well as React state. Left in state alone it was
      // thrown away by the next document snapshot, so an override dragged onto an AI trade
      // quietly reverted to the strategy's own thresholds a moment later.
      if (existing) void persistExitLevels(applyExitLevels(existing, levels));
    },
    [openPositions, persistExitLevels]
  );

  const updateCurrentPrices = useCallback((priceUpdates: { [symbol: string]: string }) => {
    setOpenPositions(prev => {
      const updatedPositions = prev.map(position => {
        const newPrice = priceUpdates[position.symbol];
        if (!newPrice) return position;

        // Only update if the price actually changed
        if (position.current === newPrice) {
          return position;
        }

        const entryPrice = parseFloat(position.entry);
        const currentPrice = parseFloat(newPrice);
        const size = parseFloat(position.size);
        const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, position.type);

        return {
          ...position,
          current: newPrice,
          pnl,
          pnlPercent,
        };
      });
      
      // Only return new array if something actually changed
      const hasChanges = updatedPositions.some((pos, index) => 
        pos.current !== prev[index].current || 
        pos.pnl !== prev[index].pnl || 
        pos.pnlPercent !== prev[index].pnlPercent
      );
      
      return hasChanges ? updatedPositions : prev;
    });
  }, [calculatePnL]);

  // Emit position updates after state changes to avoid React warnings
  useEffect(() => {
    const positionsMargin = calculateTotalUsedMargin(openPositions);
    const pendingOrdersMargin = calculatePendingOrdersMargin(pendingOrders);
    const totalUsedMargin = positionsMargin + pendingOrdersMargin;
    const totalUnrealizedPnL = calculateTotalUnrealizedPnL(openPositions);
    unrealizedPnLRef.current = totalUnrealizedPnL;
    // Only the open positions, deliberately: a resting order does not lock margin against
    // the stored balance, so counting it here would invent margin that was never taken.
    usedMarginRef.current = positionsMargin;

    // Check if values have actually changed to prevent infinite loops
    const currentValues = { totalUsedMargin, totalUnrealizedPnL, accountType };
    const prevValues = prevValuesRef.current;
    
    if (!prevValues || 
        prevValues.totalUsedMargin !== totalUsedMargin || 
        prevValues.totalUnrealizedPnL !== totalUnrealizedPnL || 
        prevValues.accountType !== accountType) {
      
      // Update the ref with current values
      prevValuesRef.current = currentValues;
      
      // Debug logging
      
      // Emit updates for both positions and pending orders
      positionEventEmitter.emit('positionsUpdated', currentValues);
    }
  }, [openPositions, pendingOrders, accountType, calculateTotalUsedMargin, calculatePendingOrdersMargin, calculateTotalUnrealizedPnL]);

  // Restate used margin and equity from the positions that are actually open.
  //
  // Two problems are solved here. Persisting is a read-modify-write against the stored
  // balance, and this used to run inline with the emit above, so it fired on every tick that
  // moved P&L: a Firestore round trip several times a second, and one that could still be in
  // flight when a position closed and then land afterwards, writing back a figure derived
  // from the balance as it was *before* the close settled. And used margin was only ever
  // adjusted by increments at open and close, so a single missed release stayed in the
  // account forever — an account with nothing open could sit there reporting hundreds of
  // dollars locked. Sending the true totals on a timer paces the writes and lets the account
  // correct itself.
  useEffect(() => {
    if (!syncPositionState || !user) return;

    const timer = setInterval(() => {
      // A close settles the account itself, from a balance it reads first. Writing over it
      // from here at the same moment would race it.
      if (closingPositionsRef.current.size > 0) return;

      // Until the stored positions have been read at least once, an empty list means "not
      // loaded yet", not "nothing open", and syncing it would release real margin.
      if (!positionsLoadedRef.current) return;

      syncPositionState(accountType, usedMarginRef.current, unrealizedPnLRef.current)
        .catch(error => console.error('Error syncing position state:', error));
    }, POSITION_SYNC_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [accountType, syncPositionState, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (prevValuesRef.current) {
        prevValuesRef.current = null;
      }
    };
  }, []);

  // Clear thresholds cache when user document or account type changes
  useEffect(() => {
    thresholdsCache.current = {};
  }, [userDocument, accountType]);

  // Function to check pending orders for execution
  const checkPendingOrdersForExecution = useCallback(() => {
    if (pendingOrders.length === 0) return;

    
    pendingOrders.forEach(order => {
      const marketData = getMarketDataForSymbol(order.symbol);
      if (marketData) {
        const currentPrice = parseFloat(marketData.price);
        const orderPrice = parseFloat(order.price);
        const stopPrice = order.stopPrice ? parseFloat(order.stopPrice) : null;
        
        // Order execution logic
        
        let shouldExecute = false;
        let executionReason = '';
        
        if (order.orderType === 'limit') {
          // Limit order logic
          if (order.type === 'Buy') {
            // Buy limit: execute when current price <= limit price
            shouldExecute = currentPrice <= orderPrice;
            executionReason = `Buy limit: ${currentPrice} <= ${orderPrice}? ${shouldExecute}`;
          } else {
            // Sell limit: execute when current price >= limit price
            shouldExecute = currentPrice >= orderPrice;
            executionReason = `Sell limit: ${currentPrice} >= ${orderPrice}? ${shouldExecute}`;
          }
        } else if (order.orderType === 'stop') {
          // Stop order logic
          if (order.type === 'Buy') {
            // Buy stop: execute when current price >= stop price
            shouldExecute = stopPrice ? currentPrice >= stopPrice : false;
            executionReason = `Buy stop: ${currentPrice} >= ${stopPrice}? ${shouldExecute}`;
          } else {
            // Sell stop: execute when current price <= stop price
            shouldExecute = stopPrice ? currentPrice <= stopPrice : false;
            executionReason = `Sell stop: ${currentPrice} <= ${stopPrice}? ${shouldExecute}`;
          }
        }
        
        if (shouldExecute) {
          fillOrder(order.id, marketData.price);
        }
      } else {
      }
    });
  }, [pendingOrders, getMarketDataForSymbol, fillOrder]);

  // Automatically update current prices and P&L when positions change
  useEffect(() => {
    if (openPositions.length === 0 && pendingOrders.length === 0) return;

    // Create price updates object from market data
    const priceUpdates: { [symbol: string]: string } = {};
    
    // Update open positions
    openPositions.forEach(position => {
      const marketData = getMarketDataForSymbol(position.symbol);
      if (marketData && marketData.price !== position.current) {
        priceUpdates[position.symbol] = marketData.price;
      }
    });

    // Check pending orders for execution immediately when dependencies change
    checkPendingOrdersForExecution();

    // Only update if we have price data that's different from current prices
    if (Object.keys(priceUpdates).length > 0) {
      updateCurrentPrices(priceUpdates);
    }
  }, [openPositions, pendingOrders, getMarketDataForSymbol, checkPendingOrdersForExecution]);

  // Periodic check for pending order execution - this ensures orders execute when market price changes
  useEffect(() => {
    if (pendingOrders.length === 0) return;

    const interval = setInterval(() => {
      
      checkPendingOrdersForExecution();
    }, 1000); // Check every second

    return () => {
      clearInterval(interval);
    };
  }, [pendingOrders.length, checkPendingOrdersForExecution]);

  // Calculate total unrealized P&L
  const totalUnrealizedPnL = calculateTotalUnrealizedPnL(openPositions);

  // Monitor all positions (AI and manual) for auto-close when profit >= PROFIT_THRESHOLD or loss >= LOSS_THRESHOLD
  useEffect(() => {
    const checkAllPositionsForAutoClose = () => {
      // Get all positions (both AI and manual) that are not currently being closed
      const allPositions = openPositions.filter(position => !closingPositions.has(position.id));
      
      // Debug logging
      if (openPositions.length > 0) {
      }
      
      // Sort positions by profit (highest profit first for priority closing)
      const sortedPositions = allPositions.sort((a, b) => {
        const aPnL = a.type === 'Buy' 
          ? (parseFloat(a.current) - parseFloat(a.entry)) * parseFloat(a.size)
          : (parseFloat(a.entry) - parseFloat(a.current)) * parseFloat(a.size);
        const bPnL = b.type === 'Buy' 
          ? (parseFloat(b.current) - parseFloat(b.entry)) * parseFloat(b.size)
          : (parseFloat(b.entry) - parseFloat(b.current)) * parseFloat(b.size);
        return bPnL - aPnL; // Highest profit first
      });
      
      sortedPositions.forEach(position => {
        // Skip if position doesn't have valid current price
        if (!position.current || position.current === '0' || position.current === '') {
          return;
        }

        // Calculate current P&L
        const entryPrice = parseFloat(position.entry);
        const currentPrice = parseFloat(position.current);
        const size = parseFloat(position.size);
        
        // Skip if any values are invalid
        if (isNaN(entryPrice) || isNaN(currentPrice) || isNaN(size)) {
          return;
        }
        
        let currentPnL: number;
        if (position.type === 'Buy') {
          currentPnL = (currentPrice - entryPrice) * size;
        } else {
          currentPnL = (entryPrice - currentPrice) * size;
        }
        
        // Debug logging for all positions
        const positionType = position.isAI ? 'AI' : 'Manual';

        // Exit levels set from the chart apply to every position, AI or manual, and are checked
        // before the AI thresholds below so an explicit level always wins. The close price is
        // the level itself rather than the last tick, so the position realises the amount the
        // level promised instead of whatever the price happened to be on this pass.
        const takeProfit = position.takeProfit ? parseFloat(position.takeProfit) : null;
        const stopLoss = position.stopLoss ? parseFloat(position.stopLoss) : null;
        const isBuyPosition = position.type === 'Buy';

        if (
          takeProfit !== null &&
          !isNaN(takeProfit) &&
          (isBuyPosition ? currentPrice >= takeProfit : currentPrice <= takeProfit)
        ) {
          console.log(
            `🎯 Take profit hit: ${position.symbol} ${position.type} at ${currentPrice} reached ${takeProfit}`
          );
          closePosition(position.id, position.takeProfit as string);
          return;
        }

        if (
          stopLoss !== null &&
          !isNaN(stopLoss) &&
          (isBuyPosition ? currentPrice <= stopLoss : currentPrice >= stopLoss)
        ) {
          console.log(
            `🛑 Stop loss hit: ${position.symbol} ${position.type} at ${currentPrice} reached ${stopLoss}`
          );
          closePosition(position.id, position.stopLoss as string);
          return;
        }

        // Auto-close if profit >= PROFIT_THRESHOLD OR loss >= LOSS_THRESHOLD
        // BUT ONLY for AI trades - manual trades should be closed manually by user
        if (position.isAI) {
          // Get thresholds from strategy configuration for this symbol
          const thresholds = getThresholdsForSymbol(position.symbol);
          const { profitThreshold, lossThreshold } = thresholds;

          // A level placed by hand on the chart replaces the strategy's threshold on that side.
          // It is checked above, so falling through to the threshold here would close the trade
          // early at whatever the strategy happened to want and the dragged level would never
          // be reached. Each side is independent: setting only a target leaves the strategy's
          // loss threshold in charge of the downside.
          const hasManualTakeProfit = takeProfit !== null && !isNaN(takeProfit);
          const hasManualStopLoss = stopLoss !== null && !isNaN(stopLoss);

          // Check for profit threshold (positive P&L)
          if (!hasManualTakeProfit && currentPnL >= profitThreshold) {
            console.log(`💰 AI Auto-Close PROFIT: ${position.symbol} ${positionType} position hit profit threshold: $${currentPnL.toFixed(2)} >= $${profitThreshold}`);
            closePosition(position.id, position.current);
            return; // Exit early to avoid double processing
          }
          
          // Check for loss threshold (negative P&L)
          if (!hasManualStopLoss && currentPnL <= -lossThreshold) {
            console.log(`🛑 AI Auto-Close LOSS: ${position.symbol} ${positionType} position hit loss threshold: $${currentPnL.toFixed(2)} <= -$${lossThreshold}`);
            closePosition(position.id, position.current);
            return; // Exit early to avoid double processing
          }
          
          // Log current status for monitoring (only occasionally to avoid spam)
          if (Math.random() < 0.1) { // 10% chance to log
            // console.log(`📊 AI Position Monitor: ${position.symbol} P&L: $${currentPnL.toFixed(2)} | Thresholds: +$${profitThreshold}/-$${lossThreshold}`);
          }
        }
      });
    };

    // Check every 500ms for all positions to auto-close (faster response)
    const interval = setInterval(checkAllPositionsForAutoClose, 500);

    return () => {
      clearInterval(interval);
    };
  }, [openPositions, closingPositions, closePosition, getThresholdsForSymbol]);

  const value: PositionContextType = {
    openPositions,
    pendingOrders,
    closedPositions,
    closingPositions,
    totalUnrealizedPnL,
    addOpenPosition,
    addPendingOrder,
    closePosition,
    cancelOrder,
    editOrder,
    fillOrder,
    setPositionExitLevels,
    updateCurrentPrices,
    testThresholdsForSymbol, // 🔥 NEW: Add test function for debugging
  };

  return (
    <PositionContext.Provider value={value}>
      {children}
    </PositionContext.Provider>
  );
};
